import { Money, GbpNote } from "../lib/currency";
import Navbar from "../components/Navbar";
import { useAuth } from "../lib/auth";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Check, Sparkles, Crown, Rocket } from "lucide-react";
import { useNavigate } from "react-router-dom";
import PromoRedeemBox from "../components/PromoRedeemBox";
import SEO from "../components/SEO";

const TIERS = [
  {
    key: "free",
    name: "Free",
    priceLabel: "£0", priceGbp: 0,
    period: "forever",
    icon: Sparkles,
    highlight: false,
    features: [
      "Browse verified creators",
      "Save favourites",
      "Up to 2 hires / month",
      "20% platform fee",
    ],
  },
  {
    key: "starter",
    name: "Starter",
    priceLabel: "£29", priceGbp: 29,
    period: "per month",
    icon: Rocket,
    highlight: true,
    features: [
      "Unlimited search & saves",
      "Unlimited hires",
      "Basic analytics",
      "In-app chat",
      "15% platform fee",
    ],
  },
  {
    key: "pro",
    name: "Pro",
    priceLabel: "£99", priceGbp: 99,
    period: "per month",
    icon: Crown,
    highlight: false,
    features: [
      "Everything in Starter",
      "Advanced filters",
      "Team seats",
      "Priority support",
      "10% platform fee (halved)",
    ],
  },
  {
    key: "founding",
    name: "Founding",
    priceLabel: "£249", priceGbp: 249,
    period: "per year",
    icon: Crown,
    highlight: false,
    features: [
      "Everything in Pro",
      "10% platform fee — locked for life",
      "Founding badge on your profile",
      "Early access to new features",
    ],
  },
];

const CREATOR_PRO = { priceGbp: 19, features: ["Unlimited Creator Studio exports", "AI auto-edit, captions & hooks", "Brand kits & templates", "Priority in Discover"] };

export default function Pricing() {
  const { user, profile } = useAuth();
  const navigate = useNavigate();

  const LOOKUP = { starter: "brand_starter_monthly", pro: "brand_pro_monthly", founding: "brand_founding_annual" };
  const subscribe = async (tier) => {
    if (!user) { navigate("/auth?mode=signup&role=brand"); return; }
    if (user.role !== "brand") { toast.error("Only brand accounts subscribe"); return; }
    if (tier === "free") { navigate("/brand"); return; }
    try {
      const r = await api.post("/payments/checkout", { lookup_key: LOOKUP[tier], origin_url: window.location.origin });
      if (r.data?.url) window.location.href = r.data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  const creatorPro = async () => {
    if (!user) { navigate("/auth?mode=signup&role=creator"); return; }
    if (user.role !== "creator") { toast.error("Creator Pro is for creator accounts"); return; }
    try {
      const r = await api.post("/payments/checkout", { lookup_key: "creator_pro_monthly", origin_url: window.location.origin });
      if (r.data?.url) window.location.href = r.data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const currentTier = profile?.subscription_tier;

  return (
    <div className="min-h-screen grain">
      <SEO
        title="Pricing · Brand Plans and Fees"
        description="Simple pricing on Nexus. Brands: Free, Starter £29/mo, Pro £99/mo or Founding £249/yr. Creators: free forever, Creator Pro £19/mo. Creators keep 100% of every payout — zero commission."
        path="/pricing"
        keywords={["influencer platform pricing", "creator marketplace fees", "influencer marketing cost", "brand subscription plan"]}
      />
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-14">
        <div className="text-center mb-14 max-w-2xl mx-auto">
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">Pricing</div>
          <h1 className="font-display font-bold text-4xl md:text-5xl tracking-tighter">
            Fair pricing — <span className="nexus-gradient-text">stacked</span>, not either/or.
          </h1>
          <p className="text-zinc-400 mt-4">
            Brand subscription + a platform fee on every hire. Higher plans cut your platform fee.
            Creators always keep 100% of the agreed price — no commission, ever.
          </p>
          <GbpNote className="mt-2" />
          <p className="hidden">
          </p>
        </div>

        <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-5" data-testid="pricing-grid">
          {TIERS.map((t) => {
            const Icon = t.icon;
            const isCurrent = currentTier === t.key;
            return (
              <div
                key={t.key}
                className={`relative p-8 rounded-3xl bg-[#0C0C10] hairline beam-hover flex flex-col ${
                  t.highlight ? "nexus-border-glow" : ""
                }`}
                data-testid={`pricing-card-${t.key}`}
              >
                {t.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 nexus-gradient text-white text-[10px] uppercase tracking-widest font-bold px-3 py-1 rounded-full">
                    Most popular
                  </div>
                )}
                <Icon className={`h-6 w-6 mb-6 ${t.highlight ? "text-nexus-pink" : "text-zinc-400"}`} />
                <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">{t.name}</div>
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="font-display font-black text-5xl tracking-tighter"><Money gbp={t.priceGbp} decimals={0} /></span>
                </div>
                <div className="text-sm text-zinc-500 mb-6">{t.period}</div>
                <ul className="space-y-2.5 mb-8 flex-1">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm">
                      <Check className="h-4 w-4 text-nexus-blue mt-0.5 shrink-0" />
                      <span className="text-zinc-200">{f}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={() => subscribe(t.key)}
                  disabled={isCurrent}
                  className={
                    t.highlight
                      ? "nexus-gradient text-white rounded-full font-semibold hover:opacity-90 border-0 w-full h-11"
                      : "bg-white/5 border border-white/15 text-white hover:bg-white/10 rounded-full w-full h-11"
                  }
                  data-testid={`subscribe-${t.key}-btn`}
                >
                  {isCurrent ? "Current plan" : t.key === "free" ? "Start free" : t.key === "founding" ? "Become a Founding brand" : `Subscribe to ${t.name}`}
                </Button>
              </div>
            );
          })}
        </div>

        <section className="mt-16 max-w-3xl mx-auto p-8 rounded-3xl bg-[#0C0C10] hairline grid md:grid-cols-[1fr_auto] gap-6 items-center" data-testid="pricing-creator-pro">
          <div>
            <div className="text-xs uppercase tracking-widest text-nexus-pink mb-2">For creators</div>
            <h2 className="font-display font-bold text-2xl tracking-tight">Nexus is free for creators — forever. Creator Pro is optional.</h2>
            <p className="text-sm text-zinc-400 mt-2">Keep 100% of every deal on the free plan. Creator Pro unlocks the full Studio for people making content every week.</p>
            <ul className="mt-4 grid sm:grid-cols-2 gap-2">
              {CREATOR_PRO.features.map((f) => <li key={f} className="flex items-start gap-2 text-sm text-zinc-200"><Check className="h-4 w-4 text-nexus-blue mt-0.5 shrink-0" />{f}</li>)}
            </ul>
          </div>
          <div className="text-center md:text-right">
            <div className="font-display font-black text-5xl tracking-tighter"><Money gbp={CREATOR_PRO.priceGbp} decimals={0} /></div>
            <div className="text-sm text-zinc-500 mb-4">per month · cancel anytime</div>
            <Button onClick={creatorPro} className="nexus-gradient text-white rounded-full font-semibold border-0 h-11 px-6" data-testid="subscribe-creator-pro-btn">
              {profile?.subscription_tier === "creator_pro" || user?.subscription_tier === "creator_pro" ? "You're on Creator Pro" : "Go Pro"}
            </Button>
          </div>
        </section>

        <div className="mt-14 text-center text-sm text-zinc-500 max-w-2xl mx-auto">
          Need agency features, team seats, or dedicated success support?{" "}
          <span className="text-white font-semibold">Contact sales</span> for a custom plan.
        </div>

        {user?.role === "brand" && (
          <div className="mt-8 max-w-md mx-auto">
            <PromoRedeemBox />
          </div>
        )}
      </div>
    </div>
  );
}
